import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { EyeOff, ScanEye } from "lucide-react";
import { SectionHead, Reveal } from "./shared";

const DATA = [
  { m: "Jan", faturamento: 92, margem: 34 },
  { m: "Fev", faturamento: 104, margem: 31 },
  { m: "Mar", faturamento: 118, margem: 27 },
  { m: "Abr", faturamento: 131, margem: 22 },
  { m: "Mai", faturamento: 149, margem: 18 },
  { m: "Jun", faturamento: 162, margem: 14 },
];

const BLIND_SPOTS = [
  "Margem real de cada lote",
  "Custo total da operação",
  "Impacto das remarcações",
  "Desempenho econômico dos lotes",
  "Ponto de equilíbrio do mês",
  "Resultado efetivo do negócio",
];

export const Problem = () => (
  <section id="problema" data-testid="problem-section" className="relative py-24 sm:py-32">
    <div className="pointer-events-none absolute left-[-180px] top-1/3 h-[380px] w-[380px] rounded-full bg-tria-vivid/10 blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="O problema"
        testId="problem-head"
        title={<>Vender mais nem sempre significa <span className="text-gradient-blue">ganhar mais</span></>}
        sub="A maioria dos negócios acompanha vendas e faturamento. Poucos enxergam o que realmente sobra da operação."
      />

      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="glass-card relative overflow-hidden rounded-2xl p-5 sm:p-7" data-testid="problem-chart">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-tria-subtle">O efeito invisível</p>
                <p className="mt-1.5 font-display text-lg font-semibold text-white">Faturamento sobe, margem escapa</p>
              </div>
              <div className="flex flex-col gap-1.5 font-mono text-[9.5px] text-tria-subtle">
                <span className="flex items-center gap-1.5"><span className="h-1.5 w-4 rounded-full bg-tria-blue" />Faturamento</span>
                <span className="flex items-center gap-1.5"><span className="h-1.5 w-4 rounded-full bg-rose-400" />Margem real</span>
              </div>
            </div>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={DATA} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "rgba(6,13,31,0.95)", border: "1px solid rgba(125,211,252,0.25)", borderRadius: 10, fontSize: 11, fontFamily: "JetBrains Mono" }} labelStyle={{ color: "#7DD3FC" }} cursor={{ stroke: "rgba(125,211,252,0.25)" }} />
                  <ReferenceLine y={30} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: "margem mínima", fill: "#F59E0B", fontSize: 9, position: "insideTopLeft" }} />
                  <Line type="monotone" dataKey="faturamento" name="Faturamento (R$ mil)" stroke="#2E7CF6" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="margem" name="Margem real (%)" stroke="#FB7185" strokeWidth={2.5} dot={false} strokeDasharray="1 0" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-4 border-t border-white/[0.07] pt-4 text-[12.5px] leading-relaxed text-tria-mist">
              Olhar apenas o faturamento mascara remarcações agressivas, lotes parados e custos
              que corroem o resultado — a sensação de lucro some no fim do mês.
            </p>
          </div>
        </Reveal>

        <div className="flex flex-col gap-4">
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-tria-mist sm:text-lg">
              Quem gere no escuro não enxerga com clareza:
            </p>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2">
            {BLIND_SPOTS.map((item, i) => (
              <Reveal key={item} delay={0.12 + i * 0.06}>
                <div data-testid={`problem-item-${i + 1}`} className="group flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3.5 transition-all duration-300 hover:border-rose-400/30 hover:bg-rose-400/[0.04]">
                  <EyeOff className="h-4 w-4 shrink-0 text-rose-400/80" />
                  <span className="text-[13.5px] font-medium text-tria-mist transition-colors group-hover:text-white">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.5}>
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-tria-sky/25 bg-tria-sky/[0.06] px-5 py-4" data-testid="problem-insight">
              <ScanEye className="h-5 w-5 shrink-0 text-tria-sky" />
              <p className="text-[13.5px] font-medium leading-relaxed text-white">
                A diferença entre controlar números e tomar decisões está em enxergar{" "}
                <span className="text-tria-sky">o que cada número significa para o resultado.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
